import { type User } from "lucide-react";
import { cn } from "@/lib/utils";
import { BOTTOM_TABS, formatCount, type BottomDockCount, type BottomTab } from "./shellState";
import type { BottomDockCounts } from "./BottomDockContent";

interface Props {
  visibleBottomTabs: typeof BOTTOM_TABS;
  bottomDockTab: BottomTab | null;
  setBottomDockTab: (t: BottomTab | null) => void;
  counts?: BottomDockCounts;
}

export function BottomTabBar({ visibleBottomTabs, bottomDockTab, setBottomDockTab, counts }: Props) {
  return (
    <div className="flex shrink-0 items-center border-t border-border/60 bg-background/80 p-2">
      <div
        role="tablist"
        className="flex h-10 w-full items-center justify-start gap-1 overflow-x-auto rounded-md bg-muted p-1 text-muted-foreground md:justify-center"
      >
        {visibleBottomTabs.map(({ id, label, Icon }: { id: BottomTab; label: string; Icon: typeof User }) => {
          const active = bottomDockTab === id;
          const rawCount = counts?.[id];
          const countLabel =
            id === "codex" || id === "builder" ? null : formatCount(rawCount as BottomDockCount | undefined);
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setBottomDockTab(active ? null : id)}
              className={cn(
                "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 md:min-w-0 md:flex-1",
                active && "bg-background text-foreground shadow-sm",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="text-xs">{label}</span>
              {countLabel && (
                <span
                  className={cn(
                    "rounded-sm px-1 text-[10px] tabular-nums",
                    active ? "bg-muted text-muted-foreground" : "text-muted-foreground/80",
                  )}
                >
                  {countLabel}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
